/**
 * StagedScopesList — read-only scope cards for the Assistent tab.
 *
 * Mirrors the overview ScopeCard visual so the user sees staged changes in
 * the same shape they'd edit them, but driven by analysis.column_mappings
 * instead of live primitives.
 *
 * Only scopes that have at least one column mapping (i.e. a new/edited/removed
 * primitive) are rendered. Each row shows the same type-icon + title layout
 * as the overview, with the analysis description on the right.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconTable } from "@tabler/icons-react"
import clsx from "clsx"
import { useCallback, useState } from "react"
import type { AnalysisColumn, ConfigurationAnalysis, Scope } from "../../types"
import {
  scopeDisplayName,
  SCOPE_ICON_COMPONENTS,
  TYPE_COLORS,
  TYPE_ICONS,
  getPrimitiveDisplayTitle,
} from "../config/scopeIcons"

interface StagedPrimitive {
  id: string
  // `name` + `scope_id` enable EN translation lookup via PRIMITIVE_DISPLAY_NAMES.
  // Missing for analysis-staged columns (CSV header only), so the helper falls
  // back to title in that case.
  name?: string
  scope_id?: string
  related_scope_id?: string | null
  title: string
  type: "string" | "number" | "date" | "relation" | "formula"
  is_pk: boolean
  description?: string
}

interface StagedScope {
  id: string
  primitives: StagedPrimitive[]
  relationCount: number
}

const SCOPE_ORDER = [
  "supplier",
  "component",
  "product",
  "customer",
  "substance",
]

function sortScopes(result: StagedScope[]): StagedScope[] {
  return result.sort((a, b) => {
    const ai = SCOPE_ORDER.indexOf(a.id)
    const bi = SCOPE_ORDER.indexOf(b.id)
    if (ai === -1 && bi === -1) return a.id.localeCompare(b.id)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

function groupByLiveScopes(scopes: Scope[]): StagedScope[] {
  const result: StagedScope[] = scopes.map((s) => {
    const prims = s.primitives ?? []
    const relationCount = prims.filter((p) => p.type === "relation").length
    return {
      id: s.id,
      primitives: prims.map((p) => ({
        id: p.id,
        name: p.name,
        scope_id: p.scope_id,
        related_scope_id: p.related_scope_id,
        title: p.title,
        type: p.type,
        is_pk: p.is_pk,
        description: p.description ?? undefined,
      })),
      relationCount,
    }
  })
  return sortScopes(result)
}

function groupByScope(analysis: ConfigurationAnalysis): StagedScope[] {
  const byScope = new Map<string, AnalysisColumn[]>()
  for (const col of analysis.column_mappings) {
    const scope = col.scope || analysis.row_scope || "unassigned"
    const arr = byScope.get(scope) ?? []
    arr.push(col)
    byScope.set(scope, arr)
  }

  const result: StagedScope[] = []
  for (const [scopeId, cols] of byScope.entries()) {
    const primitives: StagedPrimitive[] = cols.map((c, i) => {
      const type: StagedPrimitive["type"] =
        c.role === "relation"
          ? "relation"
          : c.role === "derived"
            ? "formula"
            : c.type === "number" || c.type === "date" || c.type === "formula"
              ? (c.type as StagedPrimitive["type"])
              : "string"
      return {
        id: `${scopeId}-${c.header}-${i}`,
        name: c.primitive_name,
        scope_id: scopeId,
        title: c.header,
        type,
        is_pk: c.role === "primary_key",
        description: c.description,
      }
    })
    const relationCount = cols.filter((c) => c.role === "relation").length
    result.push({ id: scopeId, primitives, relationCount })
  }
  return sortScopes(result)
}

interface Props {
  analysis?: ConfigurationAnalysis
  liveScopes?: Scope[]
}

export function StagedScopesList({ analysis, liveScopes }: Props) {
  const { translate } = useTranslation()
  const stagedScopes = analysis
    ? groupByScope(analysis)
    : groupByLiveScopes(liveScopes ?? [])
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  if (stagedScopes.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-grey4 p-6">
        <p className="text-sm italic text-grey5">
          {translate("QMS/STAGED/NO_CHANGES")}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {stagedScopes.map((s) => (
        <StagedScopeCard
          key={s.id}
          scope={s}
          isExpanded={expanded.has(s.id)}
          onToggle={() => toggle(s.id)}
        />
      ))}
    </div>
  )
}

function StagedScopeCard({
  scope,
  isExpanded,
  onToggle,
}: {
  scope: StagedScope
  isExpanded: boolean
  onToggle: () => void
}) {
  const { translate } = useTranslation()
  const Icon = SCOPE_ICON_COMPONENTS[scope.id] || IconTable
  const displayName = scopeDisplayName(scope.id, translate)
  const fieldCount = scope.primitives.length - scope.relationCount

  return (
    <div className="overflow-hidden rounded-lg border border-grey4 bg-grey2">
      {/* Header */}
      <div className="flex items-center gap-2 bg-grey1 px-4 py-3">
        <button
          onClick={onToggle}
          className="-mx-1 flex flex-1 items-center justify-between rounded-md px-2 py-0.5 text-left transition-colors hover:bg-grey3"
        >
          <div className="flex items-center gap-2">
            <Icon size={18} />
            <div>
              <h3 className="text-sm font-semibold text-white">
                {displayName}
              </h3>
              <p className="text-[11px] text-grey5">
                {translate("QMS/STAGED/FIELDS_RELATIONS", {
                  fieldCount,
                  relationCount: scope.relationCount,
                })}
              </p>
            </div>
          </div>
          <svg
            className={clsx(
              "h-4 w-4 text-grey5 transition-transform",
              isExpanded && "rotate-180",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </button>
      </div>

      {/* Rows */}
      {isExpanded && (
        <div>
          {scope.primitives.length === 0 ? (
            <p className="px-4 py-3 text-xs italic text-grey5">
              {translate("QMS/SETTINGS/NO_FIELDS_CONFIGURED")}
            </p>
          ) : (
            scope.primitives.map((p) => (
              <StagedPrimitiveRow key={p.id} prim={p} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

function StagedPrimitiveRow({ prim }: { prim: StagedPrimitive }) {
  const { currentLanguage } = useTranslation()
  const typeStyle = TYPE_COLORS[prim.type] ?? {
    bg: "bg-grey3 border-grey4",
    text: "text-grey5",
  }
  const displayTitle =
    prim.name && prim.scope_id
      ? getPrimitiveDisplayTitle(
          {
            scope_id: prim.scope_id,
            name: prim.name,
            title: prim.title,
            type: prim.type,
            related_scope_id: prim.related_scope_id,
          },
          currentLanguage,
        )
      : prim.title
  return (
    <div
      className={clsx(
        "flex items-center gap-2 border-b border-grey4/50 px-3 py-2",
        prim.is_pk && "bg-yellow5/20",
      )}
    >
      <span className="w-4 shrink-0" />
      <input
        type="checkbox"
        checked
        readOnly
        className="shrink-0 accent-yellow1 opacity-60"
      />
      {prim.is_pk && (
        <span className="bg-yellow-100 text-yellow-700 flex h-4 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold">
          PK
        </span>
      )}
      <span
        className={`rounded border px-1 py-0.5 text-[10px] font-bold ${typeStyle.bg} ${typeStyle.text} shrink-0`}
      >
        {TYPE_ICONS[prim.type] ?? "?"}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-white">
        {displayTitle}
      </span>
      {prim.description && (
        <span
          className="hidden max-w-[260px] shrink-0 truncate text-[11px] text-grey5 sm:block"
          title={prim.description}
        >
          {prim.description}
        </span>
      )}
    </div>
  )
}
